// ─── AccountPage ──────────────────────────────────────────────────────────────
// Full-screen Account Settings, matching the XD reference design.
//
// Layout: three cards side-by-side (responsive — collapses to 2-up on md and
// single column on sm). Two-Factor Authentication lives below as a full-
// width row so the existing deep link `/account#two-factor-auth` still works.
//
//   ┌──────────────────┬──────────────────┬──────────────────┐
//   │ Account Summary  │ Projects         │ Maps             │
//   │  • member since  │  • project list  │  • Map Source    │
//   │  • name/co/addr  │  • role per proj │  • Google Key    │
//   │  • email/phone   │  • Join / Create │                  │
//   └──────────────────┴──────────────────┴──────────────────┘
//   ┌────────────────────────────────────────────────────────┐
//   │ Two-Factor Authentication                              │
//   └────────────────────────────────────────────────────────┘
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Loader, Check, AlertCircle, Eye, EyeOff, ShieldCheck, ShieldOff, Shield,
  Edit2, Save,
} from 'lucide-react'
import axios from 'axios'
import useAuthStore from '../store/auth-store'
import { projectsApi } from '../lib/projectsApi'
import { AppHeader } from '../components/AppHeader'
import { ChangePasswordModal } from '../components/ChangePasswordModal'
import { CreateProjectModal } from '../components/CreateProjectModal'
import { JoinExistingProjectModal } from '../components/JoinExistingProjectModal'
import { SecureInput } from '../components/SecureInput'
import { scanInput } from '../lib/inputSecurity'

// ─── small OTP input used inline in AccountPage ───────────────────────────────
function InlineOtpInput({ value, onChange, disabled }) {
  const refs = Array.from({ length: 6 }, () => useRef(null))
  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const d = value.split(''); d[idx] = ''
      onChange(d.join(''))
      if (idx > 0) refs[idx - 1].current?.focus()
    }
  }
  const handleChange = (e, idx) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1)
    const d = value.padEnd(6, ' ').split(''); d[idx] = ch
    onChange(d.join('').replace(/ /g, ''))
    if (ch && idx < 5) refs[idx + 1].current?.focus()
  }
  const handlePaste = (e) => {
    e.preventDefault()
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    onChange(p)
    refs[Math.min(p.length, 5)].current?.focus()
  }
  return (
    <div className="flex items-center gap-1.5">
      {[0,1,2].map(i => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i]||''}
          onChange={e=>handleChange(e,i)} onKeyDown={e=>handleKey(e,i)} onPaste={handlePaste}
          className="w-9 h-10 text-center text-base font-bold border-2 border-gray-300 rounded
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition" />
      ))}
      <span className="text-gray-400 font-light">-</span>
      {[3,4,5].map(i => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i]||''}
          onChange={e=>handleChange(e,i)} onKeyDown={e=>handleKey(e,i)} onPaste={handlePaste}
          className="w-9 h-10 text-center text-base font-bold border-2 border-gray-300 rounded
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition" />
      ))}
    </div>
  )
}

const fieldCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD]'

// ─── Shared card shell ────────────────────────────────────────────────────────
// Same chrome on every section: white background, soft shadow, optional edit
// pencil in the top-right corner. The card body fills remaining height so the
// Projects card can pin its action buttons to the bottom edge regardless of
// list length.
function SectionCard({ title, onEdit, children, footer }) {
  return (
    <section className="bg-white rounded-lg shadow-sm border h-full border-gray-200 flex flex-col">
      <header className="flex items-center border-b justify-between px-6 pt-5 pb-3 mb-4">
        <h2 className="common-head25 text-xl font-bold text-[#4D4D4D]">{title}</h2>
        {onEdit && (
          <button onClick={onEdit} className="p-1.5 hover:bg-gray-100 rounded-lg transition" title="Edit">
            <Edit2 className="w-6 h-6 text-gray-500" />
          </button>
        )}
      </header>
      <div className="px-6 pb-6 flex-1">{children}</div>
      {footer && <div className="px-6 pb-6 pt-2">{footer}</div>}
    </section>
  )
}

// Field label + value pair, stacked. Used by Account Summary and Maps when
// not in edit mode. value can be a string or any react node; preformatted
// preserves line breaks (for multi-line address).
function Field({ label, hint, value, preformatted }) {
  return (
    <div>
      <p className="common-size18 text-sm font-bold text-[#4D4D4D]">
        {label}{hint && <span className="ml-1.5 text-xs font-normal text-gray-400">{hint}</span>}
      </p>
      <p className={`common-size15 text-sm text-[#4D4D4D] mt-1 ${preformatted ? 'whitespace-pre-line leading-relaxed' : ''}`}>{value}</p>
    </div>
  )
}

// ─── Account Summary section ──────────────────────────────────────────────────
function AccountSummarySection() {
  const { user, updateUser } = useAuthStore()
  const navigate = useNavigate()
  const [isEditMode, setIsEditMode]                       = useState(false)
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
  const [loading, setLoading]                             = useState(false)
  const [message, setMessage]                             = useState(null)
  const [messageType, setMessageType]                     = useState('success')
  const [formData, setFormData]                           = useState({
    first_name: '', last_name: '', company: '',
    street_address: '', city: '', state_province: '', postal_code: '',
    country: 'US', timezone: '(UTC+00:00) UTC',
    email: '', phone: '',
  })

  const memberSince = useMemo(() => {
    if (user?.date_joined) {
      const date = new Date(user.date_joined)
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    }
    return 'N/A'
  }, [user?.date_joined])

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '', last_name: user.last_name || '',
        company: user.company || '',
        street_address: user.street_address || '', city: user.city || '',
        state_province: user.state_province || '', postal_code: user.postal_code || '',
        country: user.country || 'US',
        timezone: user.timezone || '(UTC+00:00) UTC',
        email: user.email || '', phone: user.phone || '',
      })
    }
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    setMessage(null)
    const secErr = scanInput(formData.first_name) || scanInput(formData.last_name) ||
      scanInput(formData.company) || scanInput(formData.street_address) ||
      scanInput(formData.city) || scanInput(formData.state_province) || scanInput(formData.postal_code)
    if (secErr) { setMessage(secErr); setMessageType('error'); return }
    setLoading(true)
    try {
      const API_URL       = import.meta.env.VITE_API_URL          || 'https://wqlive.ezxdemo.com'
      const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH    || '/api'
      const JWT_KEY       = import.meta.env.VITE_JWT_STORAGE_KEY  || 'auth_token'
      // Check both stores: "remember me" sessions keep the token in
      // localStorage, non-remembered sessions in sessionStorage. Reading only
      // localStorage made Save fail with "token not found" for anyone who
      // logged in without ticking Remember Me (the rest of the app already
      // checks both — see auth-store getToken / settingsApi authHeaders).
      const token         = localStorage.getItem(JWT_KEY) || sessionStorage.getItem(JWT_KEY)
      if (!token) throw new Error('Authentication token not found. Please login again.')
      const response = await axios.put(
        `${API_URL}${API_BASE_PATH}/account/profile/`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      )
      const updatedUser = response.data.user || response.data
      updateUser(updatedUser)
      setMessageType('success'); setMessage('Profile updated successfully!')
      setIsEditMode(false)
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      setMessageType('error')
      setMessage(error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to save profile.')
    } finally { setLoading(false) }
  }

  const fullName = `${formData.first_name} ${formData.last_name}`.trim() || '—'
  const addressLines = [
    formData.street_address,
    [formData.city, formData.state_province && formData.postal_code ? `${formData.state_province} ${formData.postal_code}` : (formData.state_province || formData.postal_code)].filter(Boolean).join(', '),
    formData.country,
  ].filter(Boolean).join('\n') || '—'

  return (
    <SectionCard title="Account Summary" onEdit={!isEditMode ? () => setIsEditMode(true) : undefined}>
      <p className="common-size15 text-sm text-[#4D4D4D] mb-5">Member Since {memberSince}</p>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2
          ${messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {messageType === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {message}
        </div>
      )}

      {isEditMode ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <SecureInput type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} placeholder="First Name" className={fieldCls} />
            <SecureInput type="text" name="last_name"  value={formData.last_name}  onChange={handleInputChange} placeholder="Last Name"  className={fieldCls} />
          </div>
          <SecureInput type="text" name="company"        value={formData.company}        onChange={handleInputChange} placeholder="Company"        className={fieldCls} />
          <SecureInput type="text" name="street_address" value={formData.street_address} onChange={handleInputChange} placeholder="Street Address" className={fieldCls} />
          <div className="grid grid-cols-3 gap-2">
            <SecureInput type="text" name="city"           value={formData.city}           onChange={handleInputChange} placeholder="City"  className={fieldCls} />
            <SecureInput type="text" name="state_province" value={formData.state_province} onChange={handleInputChange} placeholder="State" className={fieldCls} />
            <SecureInput type="text" name="postal_code"    value={formData.postal_code}    onChange={handleInputChange} placeholder="Zip"   className={fieldCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select name="country" value={formData.country} onChange={handleInputChange} className={fieldCls + ' bg-white'}>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="UK">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="IN">India</option>
            </select>
            <select name="timezone" value={formData.timezone} onChange={handleInputChange} className={fieldCls + ' bg-white'}>
              <option value="(UTC-12:00) International Date Line West">(UTC-12:00) International Date Line West</option>
              <option value="(UTC-11:00) Midway Island, Samoa">(UTC-11:00) Midway Island, Samoa</option>
              <option value="(UTC-10:00) Hawaii">(UTC-10:00) Hawaii</option>
              <option value="(UTC-09:00) Alaska">(UTC-09:00) Alaska</option>
              <option value="(UTC-08:00) Pacific Time (US & Canada)">(UTC-08:00) Pacific Time (US & Canada)</option>
              <option value="(UTC-07:00) Mountain Time (US & Canada)">(UTC-07:00) Mountain Time (US & Canada)</option>
              <option value="(UTC-06:00) Central Time (US & Canada)">(UTC-06:00) Central Time (US & Canada)</option>
              <option value="(UTC-05:00) Eastern Time (US & Canada)">(UTC-05:00) Eastern Time (US & Canada)</option>
              <option value="(UTC+00:00) UTC">(UTC+00:00) UTC</option>
              <option value="(UTC+01:00) London, Paris, Berlin">(UTC+01:00) London, Paris, Berlin</option>
              <option value="(UTC+05:30) India Standard Time">(UTC+05:30) India Standard Time</option>
            </select>
          </div>
          <input type="email" name="email" value={formData.email} disabled className={fieldCls + ' bg-gray-50 text-gray-600 cursor-not-allowed'} />
          <input type="tel"   name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Phone Number (For 2FA)" className={fieldCls} />
          <div>
            <button
              onClick={() => { setIsEditMode(false); setIsChangePasswordModalOpen(true) }}
              className="text-[#358ECD] hover:underline text-sm"
            >Change Password</button>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={() => setIsEditMode(false)} disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition disabled:opacity-50 text-sm">Cancel</button>
            <button onClick={handleSave} disabled={loading}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#358ECD] text-white rounded-md hover:bg-[#2A78B0] disabled:bg-[#7AB1D8] font-semibold text-sm">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Field label="Name"        value={fullName} />
          <Field label="Company"     value={formData.company || '—'} />
          <Field label="Address"     value={addressLines} preformatted />
          <Field label="Time Zone"   value={formData.timezone || '—'} />
          <Field label="Email"       value={formData.email || '—'} />
          <Field label="Phone"       hint="(For 2FA)" value={formData.phone || '—'} />
          {/* Plans — link to the per-project billing / plan detail page.
              Rendered as a text link (no value alongside it, matches the
              XD reference). */}
          <div>
            <div className="common-size18 text-sm font-bold text-[#4D4D4D]">Plans</div>
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="common-size15 text-sm text-[#0866AB] hover:underline"
            >
              View Plans
            </button>
          </div>
        </div>
      )}

      <ChangePasswordModal isOpen={isChangePasswordModalOpen} onClose={() => setIsChangePasswordModalOpen(false)} />
    </SectionCard>
  )
}

// ─── Projects section ─────────────────────────────────────────────────────────
// Lists every project the signed-in user is part of, showing the project name
// + the user's role (Owner / Admin / Collaborator). Footer pins two CTAs:
// JOIN EXISTING PROJECT and CREATE NEW PROJECT.
//
// Role rendering: prefer a server-provided `role` / `user_role` / `permission`
// field if the API returns one; otherwise default to "Collaborator" so the
// row still reads meaningfully.
function ProjectsSection() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [reloadTick, setReloadTick] = useState(0)
  // Modal flags — wired to the existing CreateProjectModal and
  // JoinExistingProjectModal components so the user stays on this page
  // instead of being thrown to /projects.
  const [createOpen, setCreateOpen] = useState(false)
  const [joinOpen, setJoinOpen]     = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    projectsApi.fetchProjects(1, 100)
      .then((res) => {
        if (cancelled) return
        const list = res?.success && Array.isArray(res.data) ? res.data : []
        setProjects(list)
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load projects') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [reloadTick])

  const roleOf = (p) => {
    const raw = p?.user_role || p?.role || p?.permission || (p?.is_owner ? 'owner' : null)
    if (!raw) return 'Collaborator'
    const s = String(raw).toLowerCase()
    if (s.includes('owner') || s === 'admin_owner') return 'Owner'
    if (s.includes('admin')) return 'Admin'
    return 'Collaborator'
  }

  return (
    <SectionCard
      title="Projects"
      footer={
        <div className="space-y-2">
          <button
            onClick={() => setJoinOpen(true)}
            className="w-full px-4 py-2 bg-[#358ECD] common-size18 text-white rounded-md font-bold tracking-wide text-sm hover:bg-[#2A78B0] transition">
            JOIN EXISTING PROJECT
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="w-full px-4 py-2 bg-[#358ECD] common-size18 text-white rounded-md font-bold tracking-wide text-sm hover:bg-[#2A78B0] transition">
            CREATE NEW PROJECT
          </button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-gray-500 gap-2">
          <Loader className="w-4 h-4 animate-spin" /> Loading projects…
        </div>
      )}
      {!loading && error && <div className="text-sm text-red-600">{error}</div>}
      {!loading && !error && projects.length === 0 && (
        <div className="text-sm text-gray-500 italic">You don't have any projects yet.</div>
      )}
      {!loading && !error && projects.length > 0 && (
        <ul className="space-y-4">
          {projects.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => navigate(`/dashboard/project/${p.id}`)}
                className="text-left w-full group">
                <p className="common-size18 text-sm font-bold text-[#4D4D4D] group-hover:text-[#358ECD] transition">{p.name || `Project #${p.id}`}</p>
                <p className="common-size15 text-sm text-[#4D4D4D] mt-0.5">{roleOf(p)}</p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Modals — open via the footer CTAs. CreateProjectModal calls
          onSuccess(newProject) after the POST; we bump reloadTick so the
          list re-pulls and surfaces the new entry without a page reload. */}
      <CreateProjectModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(newProject) => {
          setCreateOpen(false)
          setReloadTick((n) => n + 1)
          // Jump straight into the new project's dashboard, matching the
          // behaviour of the existing CreateProjectModal call site in the
          // Navigation header.
          if (newProject?.id) navigate(`/dashboard/project/${newProject.id}`)
        }}
      />
      <JoinExistingProjectModal
        isOpen={joinOpen}
        onClose={() => { setJoinOpen(false); setReloadTick((n) => n + 1) }}
      />
    </SectionCard>
  )
}

// ─── Maps section ─────────────────────────────────────────────────────────────
// Two fields per the XD design:
//   • Map Source — provider selector (currently Google Maps only)
//   • Google Key — API key entry, masked in view mode (* * * * * * * *)
//
// Persisted to localStorage under `wq_map_prefs_<userId>` so each user keeps
// their own settings across browsers on the same machine.
const MAP_PREFS_KEY = (userId) => `wq_map_prefs_${userId || 'anon'}`
const DEFAULT_MAP_PREFS = { mapSource: 'Google Maps', googleKey: '' }

function MapsSection() {
  const { user } = useAuthStore()
  const userId   = user?.id
  const lsKey    = useMemo(() => MAP_PREFS_KEY(userId), [userId])
  const [prefs, setPrefs] = useState(() => {
    try { return { ...DEFAULT_MAP_PREFS, ...JSON.parse(localStorage.getItem(MAP_PREFS_KEY(userId)) || '{}') } }
    catch { return { ...DEFAULT_MAP_PREFS } }
  })
  const [isEdit, setIsEdit]       = useState(false)
  const [draft, setDraft]         = useState(prefs)
  const [showKey, setShowKey]     = useState(false)
  const [savedMsg, setSavedMsg]   = useState(null)

  useEffect(() => {
    try {
      const next = { ...DEFAULT_MAP_PREFS, ...JSON.parse(localStorage.getItem(lsKey) || '{}') }
      setPrefs(next); setDraft(next)
    } catch { /* keep defaults */ }
  }, [lsKey])

  const startEdit  = () => { setDraft(prefs); setIsEdit(true); setSavedMsg(null) }
  const cancelEdit = () => { setIsEdit(false); setSavedMsg(null) }
  const save       = () => {
    // Google Maps is the only provider that needs a key — drop any
    // stored value when the user picks a different source so we don't
    // silently keep a stale key around.
    const next = draft.mapSource === 'Google Maps' ? draft : { ...draft, googleKey: '' }
    setPrefs(next)
    try { localStorage.setItem(lsKey, JSON.stringify(next)) } catch { /* quota */ }
    setIsEdit(false)
    setSavedMsg('Map preferences saved.')
    setTimeout(() => setSavedMsg(null), 3000)
  }

  return (
    <SectionCard title="Maps" onEdit={!isEdit ? startEdit : undefined}>
      {savedMsg && (
        <div className="mb-4 p-3 rounded-lg text-sm flex items-center gap-2 bg-green-50 border border-green-200 text-green-800">
          <Check className="w-4 h-4 flex-shrink-0" /> {savedMsg}
        </div>
      )}

      {isEdit ? (
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-bold text-gray-800">Map Source</span>
            <select value={draft.mapSource} onChange={(e) => setDraft(d => ({ ...d, mapSource: e.target.value }))} className={fieldCls + ' bg-white mt-1.5'}>
              <option value="Google Maps">Google Maps</option>
              <option value="OpenStreetMap">OpenStreetMap</option>
            </select>
          </label>
          {/* Google Maps is the only provider that needs an API key —
              OpenStreetMap uses public tile servers. Hide the key input
              (and clear any stored value on Save) when the picked
              source doesn't require one. */}
          {draft.mapSource === 'Google Maps' && (
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Google Key</span>
              <div className="relative mt-1.5">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={draft.googleKey}
                  onChange={(e) => setDraft(d => ({ ...d, googleKey: e.target.value }))}
                  placeholder="Paste your Google Maps API key"
                  className={fieldCls + ' pr-9'}
                />
                <button type="button" onClick={() => setShowKey(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  title={showKey ? 'Hide key' : 'Show key'}>
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={cancelEdit} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition text-sm">Cancel</button>
            <button onClick={save} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#358ECD] text-white rounded-md hover:bg-[#2A78B0] font-semibold text-sm">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Field label="Map Source" value={prefs.mapSource || '—'} />
          {prefs.mapSource === 'Google Maps' && (
            <Field label="Google Key" value={prefs.googleKey ? '*'.repeat(Math.min(prefs.googleKey.length, 8)) : '—'} />
          )}
        </div>
      )}
    </SectionCard>
  )
}

// ─── Two-Factor Authentication section ────────────────────────────────────────
// Kept as a full-width row below the 3-card row so the existing deep link
// (#two-factor-auth) and 2FA wiring stay intact.
function TwoFactorSection({ scrollRef }) {
  const { twoFactorApi } = useAuthStore()
  const [tfa, setTfa]               = useState({ is_enabled: false, backup_codes_count: 0, has_secret: false })
  const [tfaLoading, setTfaLoading] = useState(false)
  const [tfaPanel, setTfaPanel]     = useState(null)
  const [tfaQr, setTfaQr]           = useState('')
  const [tfaBackup, setTfaBackup]   = useState([])
  const [tfaOtp, setTfaOtp]         = useState('')
  const [tfaMsg, setTfaMsg]         = useState(null)

  const fetchTfaStatus = async () => {
    try { const res = await twoFactorApi.getStatus(); setTfa(res.data) } catch { /* swallow */ }
  }
  const handleTfaSetup = async (forceReset = false) => {
    setTfaLoading(true); setTfaMsg(null)
    try { const res = await twoFactorApi.setup(forceReset); setTfaQr(res.data.qr_code); setTfaPanel('setup'); setTfaOtp('') }
    catch { setTfaMsg({ type: 'error', text: 'Failed to start setup. Please try again.' }) }
    finally { setTfaLoading(false) }
  }
  const handleTfaEnable = async () => {
    setTfaLoading(true); setTfaMsg(null)
    try {
      const res = await twoFactorApi.enable(tfaOtp)
      const codes = res.data.backup_codes || []
      setTfaBackup(codes)
      // Keep backup_codes_count in sync so the "View Backup Codes (N)" button is accurate
      setTfa(prev => ({ ...prev, is_enabled: true, backup_codes_count: codes.length }))
      setTfaPanel('backup')
      setTfaMsg({ type: 'success', text: '2FA enabled successfully.' })
    } catch { setTfaMsg({ type: 'error', text: 'Invalid code. Please try again.' }) }
    finally { setTfaLoading(false) }
  }
  const handleTfaDisable = async () => {
    setTfaLoading(true); setTfaMsg(null)
    try { await twoFactorApi.disable(); setTfaPanel(null); setTfaOtp(''); await fetchTfaStatus()
          setTfaMsg({ type: 'success', text: '2FA disabled.' }) }
    catch { setTfaMsg({ type: 'error', text: 'Failed to disable 2FA. Please try again.' }) }
    finally { setTfaLoading(false) }
  }
  const handleViewBackupCodes = async () => {
    setTfaLoading(true); setTfaMsg(null)
    try { const res = await twoFactorApi.getBackupCodes(); setTfaBackup(res.data.backup_codes || []); setTfaPanel('backup') }
    catch { setTfaMsg({ type: 'error', text: 'Failed to load backup codes.' }) }
    finally { setTfaLoading(false) }
  }
  useEffect(() => { fetchTfaStatus() }, [])

  return (
    <section ref={scrollRef} id="two-factor-auth" className="bg-white rounded-lg shadow-sm border border-gray-200 scroll-mt-24">
      <header className="flex items-center border-b justify-between px-6 pt-5 pb-3 mb-4">
        <h2 className="common-head25 text-xl font-bold text-[#4D4D4D]">Two-Factor Authentication</h2>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full
          ${tfa.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {tfa.is_enabled ? <><ShieldCheck className="w-3.5 h-3.5" />Enabled</> : <><Shield className="w-3.5 h-3.5" />Disabled</>}
        </span>
      </header>

      <div className="px-6 pb-6">
        <p className="common-size15 text-sm text-[#4D4D4D] mt-1 ">
          {tfa.is_enabled ? 'Your account is protected with Google Authenticator.' : 'Add an extra layer of security using Google Authenticator.'}
        </p>

        {tfaMsg && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm
            ${tfaMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {tfaMsg.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {tfaMsg.text}
          </div>
        )}

        {tfaPanel === null && (
          <div className="flex flex-wrap gap-2 mt-4">
            {!tfa.is_enabled ? (
              tfa.has_secret ? (
                <>
                  <button onClick={() => { setTfaPanel('reenable'); setTfaOtp(''); setTfaMsg(null) }} disabled={tfaLoading}
                    className="common-size15 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 transition"
                    style={{ backgroundColor: '#358ECD' }}>
                    <ShieldCheck className="w-4 h-4" />
                    Re-enable with Existing App
                  </button>
                  <button onClick={() => handleTfaSetup(true)} disabled={tfaLoading}
                    className="common-size15 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                    {tfaLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    Scan New QR Code
                  </button>
                </>
              ) : (
                <button onClick={() => handleTfaSetup(false)} disabled={tfaLoading}
                  className="common-size15 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 transition"
                  style={{ backgroundColor: '#358ECD' }}>
                  {tfaLoading ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Enable Google Authenticator
                </button>
              )
            ) : (
              <>
                <button onClick={() => { setTfaPanel('disable'); setTfaOtp(''); setTfaMsg(null) }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition">
                  <ShieldOff className="w-4 h-4" /> Disable 2FA
                </button>
                <button onClick={handleViewBackupCodes} disabled={tfaLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                  {tfaLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  View Backup Codes ({tfa.backup_codes_count} left)
                </button>
              </>
            )}
          </div>
        )}

        {tfaPanel === 'setup' && (
          <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-5">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">1. Scan this QR code in Google Authenticator</p>
              <div className="flex justify-center">
                <img src={tfaQr} alt="2FA QR" className="w-44 h-44 border border-gray-200 rounded-lg p-1 bg-white" />
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">2. Enter the 6-digit code to confirm</p>
              <InlineOtpInput value={tfaOtp} onChange={setTfaOtp} disabled={tfaLoading} />
            </div>
            <div className="flex gap-2">
              <button onClick={handleTfaEnable} disabled={tfaLoading || tfaOtp.length < 6}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-50 transition" style={{ backgroundColor: '#358ECD' }}>
                {tfaLoading ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Confirm & Enable'}
              </button>
              <button onClick={() => { setTfaPanel(null); setTfaOtp(''); setTfaMsg(null); setTfaQr('') }}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
            </div>
          </div>
        )}

        {tfaPanel === 'reenable' && (
          <div className="border border-blue-200 rounded-xl p-5 bg-blue-50 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Enter the 6-digit code from your Google Authenticator app</p>
            <InlineOtpInput value={tfaOtp} onChange={setTfaOtp} disabled={tfaLoading} />
            <div className="flex gap-2">
              <button onClick={handleTfaEnable} disabled={tfaLoading || tfaOtp.length < 6}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-50 transition" style={{ backgroundColor: '#358ECD' }}>
                {tfaLoading ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Re-enable 2FA'}
              </button>
              <button onClick={() => { setTfaPanel(null); setTfaOtp(''); setTfaMsg(null) }}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
            </div>
          </div>
        )}

        {tfaPanel === 'disable' && (
          <div className="border border-red-200 rounded-xl p-5 bg-red-50 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Are you sure you want to disable Two-Factor Authentication?</p>
            <p className="text-xs text-gray-500">Your account will no longer require a code when signing in.</p>
            <div className="flex gap-2">
              <button onClick={handleTfaDisable} disabled={tfaLoading}
                className="px-5 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition">
                {tfaLoading ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Yes, Disable 2FA'}
              </button>
              <button onClick={() => { setTfaPanel(null); setTfaMsg(null) }}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
            </div>
          </div>
        )}

        {tfaPanel === 'backup' && (
          <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
            <p className="text-sm font-semibold text-gray-700 mb-3">Remaining Backup Codes ({tfaBackup.length})</p>
            {tfaBackup.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No backup codes remaining. Regenerate 2FA from setup.</p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 mb-3">
                {tfaBackup.map((c, i) => (
                  <code key={i} className="text-xs bg-white border border-gray-200 font-mono px-3 py-1.5 rounded text-center text-gray-700">{c}</code>
                ))}
              </div>
            )}
            <button onClick={() => { setTfaPanel(null); setTfaMsg(null) }}
              className="text-sm text-gray-500 hover:text-gray-700 transition">Close</button>
          </div>
        )}
      </div>
    </section>
  )
}

// ─── Page shell ───────────────────────────────────────────────────────────────
export function AccountPage() {
  const location           = useLocation()
  const twoFactorSectionRef = useRef(null)
  const { user }           = useAuthStore()

  // Allow `/account#two-factor-auth` deep links to scroll the 2FA card into view.
  useEffect(() => {
    if (!user) return
    if (location.hash === '#two-factor-auth') {
      const t = setTimeout(() => {
        twoFactorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
      return () => clearTimeout(t)
    }
  }, [location.hash, user])

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-[#358ECD]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Shared white header strip (Logo · Dashboard / Data Analytics / …
          · As of <time>) — matches the chrome on DashboardPage so the
          Account page doesn't feel like a different app. */}
      <AppHeader />

      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xxl w-full py-4 px-8 mx-auto space-y-6">
          {/* 3-column row: Account Summary | Projects | Maps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            <AccountSummarySection />
            <ProjectsSection />
            <MapsSection />
          </div>

          {/* Full-width 2FA row below the 3-up grid */}
          <TwoFactorSection scrollRef={twoFactorSectionRef} />
        </div>
      </div>
    </div>
  )
}
